import React, { useEffect, useMemo, useState, useRef } from "react";
import { motion } from "framer-motion";
import { Search, MapPin, ImageOff, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { api, absUrl } from "@/lib/api";
import Seo from "@/components/Seo";

const LOCATIONS = ["All", "Denver", "Mooresville", "Cornelius", "Huntersville", "Davidson", "Sherrills Ford"];

export default function GalleryPage() {
  // Build the .sm.jpg thumbnail URL alongside the full image, for use in srcset
  const smUrl = (url) => {
    if (!url) return url;
    // strip extension and add .sm.jpg
    return url.replace(/\.(jpe?g|png|webp)$/i, ".sm.jpg");
  };

  const [q, setQ] = useState("");
  const [location, setLocation] = useState("All");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [slideIdx, setSlideIdx] = useState(0);
  const scrollRef = useRef(null);

  const slideCount = expanded
    ? 1 + (expanded.after_url || expanded.image_url ? 1 : 0) + (expanded.after_url_2 ? 1 : 0)
    : 0;

  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth, behavior: "smooth" });
  };

  const onLightboxScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    if (idx !== slideIdx) setSlideIdx(idx);
  };

  useEffect(() => {
    if (!expanded) return;
    setSlideIdx(0);
    const onKey = (e) => { if (e.key === "Escape") setExpanded(null); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [expanded]);

  const fetchItems = useMemo(
    () => async () => {
      setLoading(true);
      try {
        const params = {};
        if (q) params.q = q;
        if (location !== "All") params.location = location;
        const { data } = await api.get("/gallery", { params });
        setItems(data);
      } finally {
        setLoading(false);
      }
    },
    [q, location]
  );

  useEffect(() => {
    const t = setTimeout(fetchItems, 200);
    return () => clearTimeout(t);
  }, [fetchItems]);

  return (
    <div data-testid="gallery-page">
      <Seo
        title="Project Gallery — Lake Norman Landscapes"
        description="Browse Bloomers' recent yard and garden installations across Denver, Mooresville, and Cornelius. Filter by location."
        path="/gallery"
      />
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pt-16 pb-4">
        <span className="pill-tag bg-[#C0AD21]/15 text-[#8a7a10]">Gallery</span>
        <h1 className="mt-3 text-5xl sm:text-6xl font-black tracking-tight">
          Search recent <span className="text-[#21C078]">projects</span>.
        </h1>
        <p className="mt-4 max-w-2xl text-lg text-[#1C1C1C]/75 leading-relaxed">
          Filter by Lake Norman location to find inspiration close to home.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-8">
        <div className="card p-4 sm:p-5 flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#1C1C1C]/50" />
            <Input
              data-testid="gallery-search-input"
              placeholder="Search projects…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-10 h-12 rounded-full border-[#21C078]/20 focus-visible:ring-[#21C078]"
            />
          </div>

          <Select value={location} onValueChange={setLocation}>
            <SelectTrigger data-testid="gallery-filter-location" className="h-12 md:w-56 rounded-full border-[#21C078]/20">
              <MapPin className="w-4 h-4 mr-2 text-[#219DC0]" />
              <SelectValue placeholder="Location" />
            </SelectTrigger>
            <SelectContent>
              {LOCATIONS.map((l) => (
                <SelectItem key={l} value={l} data-testid={`location-option-${l.toLowerCase()}`}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="mt-10 text-center text-[#1C1C1C]/60">Loading projects…</div>
        ) : items.length === 0 ? (
          <div data-testid="gallery-empty" className="mt-16 text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-[#21C078]/10 grid place-items-center text-[#21C078]">
              <ImageOff className="w-8 h-8" />
            </div>
            <h3 className="mt-4 text-2xl font-black">No projects yet</h3>
            <p className="text-[#1C1C1C]/60 max-w-md mx-auto mt-2">
              Our admin is curating recent installs. Check back soon — or reach out for a private tour of ongoing designs.
            </p>
          </div>
        ) : (
          <>
          <div className="mt-8 grid sm:grid-cols-1 lg:grid-cols-2 gap-6">
            {items.map((it, i) => {
              const before = it.before_url;
              const after = it.after_url || it.image_url;
              const hasPair = Boolean(before && after);
              return (
                <motion.article
                  key={it.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: (i % 6) * 0.05 }}
                  className="card overflow-hidden cursor-zoom-in text-left"
                  data-testid={`gallery-item-${it.id}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => setExpanded(it)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setExpanded(it); } }}
                >
                  {hasPair ? (
                    <div className={`grid grid-cols-1 ${it.after_url_2 ? "sm:grid-cols-3" : "sm:grid-cols-2"} gap-0.5 bg-white`}>
                      <div className="aspect-[3/2] sm:aspect-[4/5] relative overflow-hidden">
                        <img
                          src={absUrl(smUrl(before))}
                          alt={`${it.title} — before`}
                          width="800"
                          height="1000"
                          loading="eager"
                          decoding="async"
                          className="w-full h-full object-cover object-right-bottom hover:scale-105 transition-transform duration-700"
                          data-testid={`gallery-before-${it.id}`}
                        />
                        <span className="absolute top-2 left-2 pill-tag bg-black/60 text-white text-[10px] uppercase tracking-widest">Before</span>
                      </div>
                      <div className="aspect-[3/2] sm:aspect-[4/5] relative overflow-hidden">
                        <img
                          src={absUrl(smUrl(after))}
                          alt={`${it.title} — after`}
                          width="800"
                          height="1000"
                          loading="eager"
                          decoding="async"
                          className="w-full h-full object-cover object-right-bottom hover:scale-105 transition-transform duration-700"
                          data-testid={`gallery-after-${it.id}`}
                        />
                        <span className="absolute top-2 left-2 pill-tag bg-[#21C078] text-white text-[10px] uppercase tracking-widest">After</span>
                      </div>
                      {it.after_url_2 && (
                        <div className="aspect-[3/2] sm:aspect-[4/5] relative overflow-hidden">
                          <img
                            src={absUrl(smUrl(it.after_url_2))}
                            alt={`${it.title} — after 2`}
                            width="800"
                            height="1000"
                            loading="eager"
                            decoding="async"
                            className="w-full h-full object-cover object-right-bottom hover:scale-105 transition-transform duration-700"
                            data-testid={`gallery-after-2-${it.id}`}
                          />
                          <span className="absolute top-2 left-2 pill-tag bg-[#21C078] text-white text-[10px] uppercase tracking-widest">After</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="aspect-[4/3] overflow-hidden bg-white relative">
                      <img
                        src={absUrl(after || before)}
                        alt={it.title}
                        width="800"
                        height="600"
                        loading="eager"
                        decoding="async"
                        className="w-full h-full object-cover object-right-bottom hover:scale-105 transition-transform duration-700"
                      />
                      {before && !it.after_url && (
                        <span className="absolute top-2 left-2 pill-tag bg-black/60 text-white text-[10px] uppercase tracking-widest">Before</span>
                      )}
                      {(it.after_url || (!before && after)) && (
                        <span className="absolute top-2 left-2 pill-tag bg-[#21C078] text-white text-[10px] uppercase tracking-widest">After</span>
                      )}
                    </div>
                  )}
                  <div className="p-5">
                    <div className="flex items-center gap-2">
                      <span className="pill-tag bg-[#21C078]/10 text-[#21C078]">{it.location}</span>
                    </div>
                    {it.title && <h3 className="mt-3 text-lg font-black">{it.title}</h3>}
                    {it.description && <p className={`${it.title ? "mt-1" : "mt-3"} text-sm text-[#1C1C1C]/70 leading-relaxed`}>{it.description}</p>}
                  </div>
                </motion.article>
              );
            })}
          </div>

          {expanded && (
            <div
              className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm grid place-items-center p-4 sm:p-8 overflow-y-auto"
              data-testid="gallery-lightbox"
              role="dialog"
              aria-modal="true"
              onClick={() => setExpanded(null)}
            >
              <div
                className="bg-white rounded-3xl w-full max-w-4xl overflow-hidden shadow-2xl max-h-[90vh] hide-scrollbar"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between px-6 py-4 border-b border-[#1C1C1C]/10">
                  <div>
                    {expanded.title && <h3 className="text-xl sm:text-2xl font-black">{expanded.title}</h3>}
                    <div className={`${expanded.title ? "mt-1" : ""} flex items-center gap-2`}>
                      <span className="pill-tag bg-[#21C078]/10 text-[#21C078]">{expanded.location}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setExpanded(null)}
                    className="w-10 h-10 grid place-items-center rounded-full hover:bg-[#1C1C1C]/5"
                    aria-label="Close"
                    data-testid="gallery-lightbox-close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="relative bg-black">
                  <div
                    ref={scrollRef}
                    className="flex overflow-x-auto snap-x snap-mandatory bg-black hide-scrollbar"
                    data-testid="gallery-lightbox-scroll"
                    style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                    onScroll={onLightboxScroll}
                  >
                  <div className="flex-none w-full snap-center flex flex-col bg-black" data-testid="gallery-lightbox-before-tile">
                    <div className="relative h-[60vh] bg-black">
                      {expanded.before_url ? (
                        <img
                          src={absUrl(expanded.before_url)}
                          alt={`${expanded.title} — before`}
                          className="w-full h-full object-contain"
                          data-testid="gallery-lightbox-before"
                        />
                      ) : (
                        <div className="absolute inset-0 grid place-items-center text-white/60 text-sm">No before photo</div>
                      )}
                      <span className="absolute top-3 left-3 pill-tag bg-black/70 text-white text-xs uppercase tracking-widest z-10">Before</span>
                    </div>
                    {expanded.before_caption && (
                      <div className="bg-black/70 text-white text-sm px-6 py-3 backdrop-blur-sm" data-testid="gallery-lightbox-before-caption">
                        {expanded.before_caption}
                      </div>
                    )}
                  </div>
                  <div className="flex-none w-full snap-center flex flex-col bg-black" data-testid="gallery-lightbox-after-tile">
                    <div className="relative h-[60vh] bg-black">
                      {(expanded.after_url || expanded.image_url) ? (
                        <img
                          src={absUrl(expanded.after_url || expanded.image_url)}
                          alt={`${expanded.title} — after`}
                          className="w-full h-full object-contain"
                          data-testid="gallery-lightbox-after"
                        />
                      ) : (
                        <div className="absolute inset-0 grid place-items-center text-white/60 text-sm">No after photo</div>
                      )}
                      <span className="absolute top-3 left-3 pill-tag bg-[#21C078] text-white text-xs uppercase tracking-widest z-10">After</span>
                    </div>
                    {expanded.after_caption && (
                      <div className="bg-black/70 text-white text-sm px-6 py-3 backdrop-blur-sm" data-testid="gallery-lightbox-after-caption">
                        {expanded.after_caption}
                      </div>
                    )}
                  </div>
                  {expanded.after_url_2 && (
                    <div className="flex-none w-full snap-center flex flex-col bg-black" data-testid="gallery-lightbox-after-2-tile">
                      <div className="relative h-[60vh] bg-black">
                        <img
                          src={absUrl(expanded.after_url_2)}
                          alt={`${expanded.title} — after 2`}
                          className="w-full h-full object-contain"
                          data-testid="gallery-lightbox-after-2"
                        />
                        <span className="absolute top-3 left-3 pill-tag bg-[#21C078] text-white text-xs uppercase tracking-widest z-10">After</span>
                      </div>
                      {expanded.after_caption_2 && (
                        <div className="bg-black/70 text-white text-sm px-6 py-3 backdrop-blur-sm" data-testid="gallery-lightbox-after-2-caption">
                          {expanded.after_caption_2}
                        </div>
                      )}
                    </div>
                  )}
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); scrollBy(-1); }}
                    aria-label="Previous photo"
                    data-testid="gallery-lightbox-arrow-prev"
                    className={`absolute top-1/2 left-3 -translate-y-1/2 z-20 grid place-items-center w-11 h-11 rounded-full bg-white/85 hover:bg-white text-[#1C1C1C] shadow-lg transition-opacity ${slideIdx === 0 ? "opacity-0 pointer-events-none" : "opacity-100"}`}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); scrollBy(1); }}
                    aria-label="Next photo"
                    data-testid="gallery-lightbox-arrow-next"
                    className={`absolute top-1/2 right-3 -translate-y-1/2 z-20 grid place-items-center w-11 h-11 rounded-full bg-white/85 hover:bg-white text-[#1C1C1C] shadow-lg transition-opacity ${slideIdx >= slideCount - 1 ? "opacity-0 pointer-events-none" : "opacity-100"}`}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
                {expanded.description && (
                  <div className="px-6 py-5 text-[#1C1C1C]/75 leading-relaxed">{expanded.description}</div>
                )}
              </div>
            </div>
          )}
          </>
        )}
      </section>
    </div>
  );
}
